import { boundingExtent } from 'ol/extent';
import { Layer } from 'ol/layer';

/**
 * Maneja clics en el mapa para obtener información de las entidades y ajustar la vista.
 * - Extrae las propiedades de la entidad clicada y las almacena en `groupedData`.
 * - Si es un clúster, obtiene el slug de la especie como categoría.
 * - Si no, usa la categoría de la capa.
 * - Agrupa todas las features por categoría antes de añadirlas al estado.
 * - Filtra y almacena las propiedades relevantes, excluyendo la geometría.
 * - Ajusta la vista del mapa al área de la geometría si es válida.
 */
export function handleMapClickGeoJSON(event, map, setFeatures) {
    // Objeto para agrupar features por categoría
    const featuresByCategory = {};
    let firstGeometry = null; // Para ajustar la vista solo una vez

    map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
            if (!layer.getVisible() || layer.get('queryable') !== true) return;
            
            if (layer.getVisible() && feature) {
                const properties_layer = feature.getProperties();

                let featuresArray = [];
                let layerCategory = null;

                if (properties_layer.features && properties_layer.features.length > 0) {
                    // Es un clúster
                    featuresArray = properties_layer.features.map(f => {
                        const props = f.getProperties();
                        delete props.geometry;
                        return props;
                    });

                    // Usar el slug de la primera feature como categoría
                    const nestedFeature = properties_layer.features[0];
                    if (nestedFeature) {
                        const species = nestedFeature.get('species');
                        const slug = species?.categories?.[0]?.slug;
                        layerCategory = (typeof slug === 'string' ? slug.toUpperCase() : null) || layer.get('category') || layer.get('title') || layer.get('name') || 'No category';
                    } else {
                        // Si no hay nestedFeature, usar el nombre de la capa
                        layerCategory = layer.get('category') || layer.get('title') || layer.get('name') || 'No category';
                    }
                } else {
                    // Feature individual
                    const props = { ...properties_layer };
                    delete props.geometry;
                    // Almacenar referencia al feature original y layer para edición
                    props._olFeature = feature;
                    props._olLayer = layer;
                    featuresArray.push(props);

                    // Usar la categoría o título de la capa
                    layerCategory = layer.get('category') || layer.get('title') || layer.get('name') || 'No category';
                }

                // Agrupar features por categoría
                if (!featuresByCategory[layerCategory]) {
                    featuresByCategory[layerCategory] = [];
                }
                featuresByCategory[layerCategory].push(...featuresArray);

                // Guardar la primera geometría para ajustar la vista
                if (!firstGeometry) {
                    firstGeometry = feature.getGeometry();
                }
            }
        },
        { hitTolerance: 15 } // Aumentar el margen de error para que se detecte el feature al pulsar sobre el movil (en pixeles), fallaba en chrome
    );

    // Convertir el objeto agrupado al formato esperado por el estado
    const groupedData = Object.entries(featuresByCategory).map(([category, features]) => ({
        [category]: features
    }));

    // Enviar todos los datos agrupados al estado global de features
    if (groupedData.length > 0) {
        setFeatures(prev => [...prev, ...groupedData]);
    }

    // Ajuste de vista al área del primer feature (solo una vez)
    if (firstGeometry) {
        const coordinates = firstGeometry.getCoordinates();

        if (Array.isArray(coordinates) && coordinates.length > 0) {
            const extentCoordinates = extractValidCoordinates(firstGeometry);
            if (extentCoordinates.length >= 2) {
                const extent = boundingExtent(extentCoordinates);
                if (!isEmptyExtent(extent)) {
                    map.getView().fit(extent, {
                        duration: 1000,
                        padding: [50, 50, 50, 50],
                        maxZoom: 14,
                    });
                }
            }
        }
    }
}

/**
 * Extrae coordenadas planas de una geometría, incluyendo MultiPolygon y MultiLineString.
 * Utiliza el método apropiado según el tipo de geometría para extraer todos los vértices.
 *
 * @param {ol.geom.Geometry} geometry - Geometría de OpenLayers
 * @returns {Array} Array plano de coordenadas
 */
function extractValidCoordinates(geometry) {
    let coordinates = geometry.getCoordinates();
    let flatCoordinates = [];

    if (geometry.getType() === "MultiPolygon") {
        coordinates.forEach(polygon => {
            polygon.forEach(ring => {
                flatCoordinates.push(...ring);
            });
        });
    } else if (geometry.getType() === "MultiLineString") {
        coordinates.forEach(line => {
            flatCoordinates.push(...line);
        });
    } else {
        while (Array.isArray(coordinates[0])) {
            coordinates = coordinates[0];
        }
        flatCoordinates = coordinates;
    }

    return flatCoordinates;
}

/**
 * Verifica si un extent es inválido o vacío.
 *
 * @param {Array<number>} extent - Array con 4 valores [minX, minY, maxX, maxY]
 * @returns {boolean} Verdadero si el extent es inválido
 */
function isEmptyExtent(extent) {
    return (
        !extent || extent.length !== 4 ||
        (extent[0] === extent[2] && extent[1] === extent[3]) ||
        extent.includes(Infinity) || extent.includes(-Infinity)
    );
}
